import { useEffect, useState } from "react"
import { useResolveAssetsMutation } from "~/ui-rtk/api/assetsApi"
import { useAppDispatch, useAppSelector } from "~/ui-rtk/app/hooks"
import { cacheAssetUrl } from "~/ui-rtk/app/slices/asset-cache.slice"
import ImagePlaceholder from "~/ui-rtk/components/ui/common/ImagePlaceholder"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { isVideoUrl } from "./helpers"

export type AssetSourceType =
  | "FACEBOOK_AD"
  | "TIKTOK_AD"
  | "SHOPIFY_PRODUCT"
  | "GEN_AI"
  | "UPLOAD"
  | "INSTAGRAM"
  | "TIKTOK"
  | "YOUTUBE"
  | "YOUTUBE_AD"

export type MarathonImageProps = {
  /** The source platform/channel (e.g., "FACEBOOK_AD", "INSTAGRAM", "TIKTOK_AD") */
  sourceType: AssetSourceType
  /**
   * External reference ID from the source platform
   * Examples: ad_id, media_id, creative_id, or any external identifier
   */
  externalRefId: string
  /**
   * Optional fallback URL for the image.
   * Used in two scenarios:
   * 1. As a hint for the resolve endpoint if asset doesn't exist yet
   * 2. As a fallback when asset is not synced (PENDING_DOWNLOAD or FAILED)
   */
  url?: string
  /** Whether to display the thumbnail version instead of full-size image */
  useThumbnail?: boolean
  /** Alt text for the image (accessibility) */
  alt?: string
  /** Custom CSS classes */
  className?: string
  /** Fixed size (sets both width and height) */
  size?: number
  /** Image width */
  width?: number
  /** Image height */
  height?: number
}

export default function MarathonImage({
  sourceType,
  externalRefId,
  url,
  useThumbnail = false,
  alt = "",
  className,
  size,
  width,
  height,
}: MarathonImageProps) {
  const dispatch = useAppDispatch()
  const cacheKey = `${sourceType}:${externalRefId}`
  const cachedUrl = useAppSelector(
    state => state.assetCacheSlice.urls[cacheKey],
  )

  const [resolveAssets] = useResolveAssetsMutation()
  const [imageUrl, setImageUrl] = useState<string | null>(cachedUrl ?? null)
  const [isImageLoaded, setIsImageLoaded] = useState(!!cachedUrl)

  useEffect(() => {
    if (cachedUrl) {
      setImageUrl(cachedUrl)
      return
    }

    let isMounted = true

    const fetchAsset = async () => {
      try {
        setImageUrl(null)
        setIsImageLoaded(false)

        const result = await resolveAssets({
          assets: [
            {
              sourceType,
              externalRefId,
              ...(url && { url }),
            },
          ],
        }).unwrap()

        const resolvedAsset = result.results[cacheKey]

        if (isMounted) {
          let resolvedUrl: string | null = null

          if (resolvedAsset) {
            if (resolvedAsset.syncStatus === "SYNCED") {
              const assetUrl = useThumbnail
                ? resolvedAsset.thumbnailUrl
                : resolvedAsset.url
              resolvedUrl = assetUrl || url || null
            } else if (url) {
              resolvedUrl = url
            }
          }

          if (resolvedUrl) {
            setImageUrl(resolvedUrl)
            dispatch(cacheAssetUrl({ key: cacheKey, url: resolvedUrl }))
          }
        }
      } catch {
        if (isMounted && url) {
          setImageUrl(url)
          dispatch(cacheAssetUrl({ key: cacheKey, url }))
        }
      }
    }

    void fetchAsset()

    return () => {
      isMounted = false
    }
  }, [
    sourceType,
    externalRefId,
    url,
    useThumbnail,
    resolveAssets,
    cachedUrl,
    cacheKey,
    dispatch,
  ])

  const showPlaceholder = !isImageLoaded || !imageUrl

  return (
    <div
      className={cn("relative w-full h-full overflow-hidden group", className)}
      style={{ width: size || width, height: size || height }}
    >
      {showPlaceholder && (
        <div className="absolute inset-0 z-10 animate-pulse">
          <ImagePlaceholder />
        </div>
      )}

      {imageUrl &&
        (isVideoUrl(imageUrl) ? (
          <video
            src={imageUrl}
            controls
            playsInline
            aria-label={alt}
            className={cn(
              "w-full h-full object-contain transition-opacity duration-300",
              isImageLoaded ? "opacity-100" : "opacity-0",
            )}
            onLoadedData={() => setIsImageLoaded(true)}
            onError={() => setIsImageLoaded(false)}
          />
        ) : (
          <img
            src={imageUrl}
            alt={alt}
            className={cn(
              "w-full h-full object-contain transition-opacity duration-300",
              isImageLoaded ? "opacity-100" : "opacity-0",
            )}
            onLoad={() => setIsImageLoaded(true)}
            onError={() => setIsImageLoaded(false)}
          />
        ))}
    </div>
  )
}
